import { useCallback, useRef, useState } from "react";

type MarkerElement = google.maps.marker.AdvancedMarkerElement;

interface UseMapTooltipOptions {
  getMarker: (key: string) => MarkerElement | null;
}

/** Delay (ms) before hiding the tooltip after mouse leaves the marker. */
const HIDE_DELAY = 150;

export function useMapTooltip({ getMarker }: UseMapTooltipOptions) {
  const [tooltipLocationId, setTooltipLocationId] = useState<string | null>(null);
  const [anchorElement, setAnchorElement] = useState<MarkerElement | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Detect touch device — disable tooltips on coarse pointer
  const isTouchDevice = typeof window !== "undefined"
    && window.matchMedia("(pointer: coarse)").matches;

  const onMarkerMouseEnter = useCallback(
    (id: string) => {
      if (isTouchDevice) return;

      // Cancel any pending hide when re-entering
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }

      const marker = getMarker(id);
      // Only show tooltip if marker is visible on map (not clustered)
      if (marker && marker.map !== null) {
        setTooltipLocationId(id);
        setAnchorElement(marker);
      }
    },
    [isTouchDevice, getMarker]
  );

  const onMarkerMouseLeave = useCallback(() => {
    if (isTouchDevice) return;

    // Small delay so the tooltip doesn't flicker when moving between marker and tooltip
    hideTimerRef.current = setTimeout(() => {
      setTooltipLocationId(null);
      setAnchorElement(null);
      hideTimerRef.current = null;
    }, HIDE_DELAY);
  }, [isTouchDevice]);

  const closeTooltip = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    setTooltipLocationId(null);
    setAnchorElement(null);
  }, []);

  return {
    tooltipLocationId,
    anchorElement,
    onMarkerMouseEnter,
    onMarkerMouseLeave,
    closeTooltip,
  };
}
